// MSSQL CHECK (ISJSON(col) = 1) detector.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice A3.
//
// Operates on `sys.check_constraints.definition` strings — the engine returns
// the raw CHECK expression text. The MSSQL schema cache currently does NOT
// expose check-constraint text (no columns at all in v1 — see PROGRESS.md
// Codebase Patterns), so today this detector is exercised at the unit-test
// level only. Once the MSSQL schema cache surfaces column + check metadata
// (Phase G3 / a follow-up MSSQL columns slice), the projector will call
// `detectMssqlIsJsonColumns` with the fetched constraint clauses.
//
// Recognised forms (case-insensitive):
//   CHECK (ISJSON(col) = 1)
//   CHECK (ISJSON(col) > 0)
//   CHECK (ISJSON([col]) = 1)
//   CHECK (ISJSON("col") = 1)
//   ISJSON(col) = 1     (raw definition without the CHECK ( … ) wrapper —
//                       sys.check_constraints.definition includes the wrapper
//                       inconsistently across SQL Server versions)
//   (isjson([col])=(1))  (the actual canonical sys.check_constraints.definition
//                         form — the engine wraps numeric literals in parens
//                         and lower-cases function names)
//
// Compound CHECKs (`ISJSON(col) = 1 AND something_else`) are intentionally
// skipped — JCS-A3 acceptance scopes us to `= 1` / `> 0` exact forms.

const ISJSON_CHECK = /ISJSON\s*\(\s*([\w"[\]]+)\s*\)\s*(?:=\s*\(?\s*1\s*\)?|>\s*\(?\s*0\s*\)?)/gi;

function unbracketIdentifier(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length < 2) return trimmed;
  const first = trimmed.charAt(0);
  const last = trimmed.charAt(trimmed.length - 1);
  if (first === '[' && last === ']') return trimmed.slice(1, -1).replace(/]]/g, ']');
  if (first === '"' && last === '"') return trimmed.slice(1, -1).replace(/""/g, '"');
  return trimmed;
}

export function detectMssqlIsJsonColumns(
  checkClauses: readonly string[] | null | undefined,
): ReadonlySet<string> {
  if (!checkClauses || checkClauses.length === 0) return EMPTY;
  const out = new Set<string>();
  for (const clause of checkClauses) {
    if (!clause) continue;
    if (isCompoundCheck(clause)) continue;
    let match: RegExpExecArray | null;
    ISJSON_CHECK.lastIndex = 0;
    while ((match = ISJSON_CHECK.exec(clause)) !== null) {
      const raw = match[1];
      if (!raw) continue;
      const name = unbracketIdentifier(raw);
      if (name) out.add(name);
    }
  }
  return out.size === 0 ? EMPTY : out;
}

function isCompoundCheck(clause: string): boolean {
  const stripped = clause.replace(ISJSON_CHECK, '');
  return /\b(AND|OR)\b/i.test(stripped);
}

const EMPTY: ReadonlySet<string> = new Set<string>();
